import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { WHATSAPP_URL } from '../../constants/whatsapp';

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  {
    label: 'Products',
    dropdown: [
      { label: 'Medical Gloves', href: '/medical-gloves' },
      { label: 'Food Grade Gloves', href: '/food-grade-gloves' },
      { label: 'Lab Gloves', href: '/lab-gloves' },
      { label: 'Industrial Gloves', href: '/industrial-gloves' },
    ],
  },
  { label: 'Bulk & Wholesale', href: '/bulk-wholesale' },
  { label: 'Request Quote', href: '/request-quote' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
        {/* Logo + Badge */}
        <a href="/" onClick={handleHomeClick} className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/f61ec8f8-da21-40a5-ac46-7cf45d15eb9b.png"
            alt="omandisposables.com Logo"
            className="h-10 w-auto object-contain"
          />
          <div className="flex flex-col leading-tight">
            <span
              className={`font-heading font-bold text-sm md:text-base transition-colors ${
                scrolled ? 'text-navy' : 'text-white'
              }`}
            >
              omandisposables.com
            </span>
            <span className="text-xs font-medium text-brand-green tracking-wide">
              Right Gloves. Right Price.
            </span>
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) =>
            link.dropdown ? (
              <div key={link.label} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className={`font-heading font-semibold text-sm flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap ${
                    scrolled ? 'text-navy hover:text-brand-green' : 'text-white hover:text-brand-green'
                  }`}
                >
                  {link.label}
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-4 py-2.5 text-sm font-medium text-navy hover:bg-navy/5 hover:text-brand-green transition-colors cursor-pointer"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : link.href === '/' ? (
              <a
                key={link.label}
                href="/"
                onClick={handleHomeClick}
                className={`font-heading font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-brand-green' : 'text-white hover:text-brand-green'
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href!}
                className={`font-heading font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy hover:text-brand-green' : 'text-white hover:text-brand-green'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp text-sm px-4 py-2 rounded-full"
          >
            <i className="ri-whatsapp-line text-base" />
            WhatsApp
          </a>
        </nav>

        {/* Mobile toggle */}
        <button
          className={`md:hidden text-2xl cursor-pointer transition-colors ${
            scrolled ? 'text-navy' : 'text-white'
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav-menu"
        >
          <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-line'} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div id="mobile-nav-menu" className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div key={link.label}>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 pt-3 pb-1">
                    Products
                  </p>
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-3 py-2 text-sm font-medium text-navy hover:text-brand-green cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ) : link.href === '/' ? (
                <a
                  key={link.label}
                  href="/"
                  onClick={handleHomeClick}
                  className="block px-3 py-2.5 text-sm font-semibold text-navy hover:text-brand-green cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href!}
                  className="block px-3 py-2.5 text-sm font-semibold text-navy hover:text-brand-green cursor-pointer"
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="pt-3 pb-1">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp w-full justify-center text-sm"
              >
                <i className="ri-whatsapp-line" />
                WhatsApp for Pricing
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
